import { type ReactElement, type ReactNode, type Ref, forwardRef } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  type DialogProps,
  Slide,
  type SxProps,
  type Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { type TransitionProps } from '@mui/material/transitions';
import { Save } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

import { DialogStickyHeader } from './DialogStickyHeader';

export const SlideUpTransition = forwardRef(function SlideUpTransition(
  props: TransitionProps & {
    children: ReactElement;
  },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export type SlideDialogProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footerActions?: ReactNode;
  onSave?: () => void;
  loading?: boolean;
  maxWidth?: DialogProps['maxWidth'];
  fullWidth?: boolean;
  dialogProps?: Partial<DialogProps>;
  secondaryActions?: ReactNode;
  saveDisabled?: boolean;
  cancelText?: string;
  saveText?: string;
  saveIcon?: ReactNode;
  customPrimaryActions?: ReactNode;
  disableCancel?: boolean;
  saveColor?: 'primary' | 'error' | 'success' | 'warning' | 'info';
  sx?: SxProps<Theme>;
};

export const SlideDialog = ({
  open,
  onClose,
  title,
  children,
  footerActions,
  onSave,
  loading = false,
  maxWidth = 'sm',
  fullWidth = true,
  dialogProps,
  secondaryActions,
  saveDisabled = false,
  cancelText,
  saveText,
  saveIcon,
  customPrimaryActions,
  disableCancel = false,
  saveColor = 'primary',
  sx,
}: SlideDialogProps) => {
  const { t } = useLocale();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const footerJustify = isMobile
    ? 'stretch'
    : secondaryActions
    ? 'space-between'
    : 'flex-end';

  const defaultFooter = (
    <Box
      sx={{
        p: isMobile ? 2 : 1,
        px: 2,
        borderTop: theme =>
          `1px solid ${(theme.vars ?? theme).palette.divider}`,
        backgroundColor: theme =>
          (theme.vars ?? theme).palette.background.paper,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: footerJustify,
        alignItems: isMobile ? 'stretch' : 'center',
      }}
    >
      {secondaryActions && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            order: isMobile ? 2 : 1,
            justifyContent: isMobile ? 'center' : 'flex-start',
            mt: isMobile ? 1 : 0,
            width: isMobile ? '100%' : 'auto',
          }}
        >
          {secondaryActions}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          order: isMobile ? 1 : 2,
          flexDirection: isMobile ? 'column-reverse' : 'row',
          width: isMobile ? '100%' : 'auto',
        }}
      >
        {!disableCancel && (
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            fullWidth={isMobile}
          >
            {cancelText ?? t('Cancel')}
          </Button>
        )}
        {customPrimaryActions ??
          (onSave && (
            <LoadingButton
              loading={loading}
              variant="contained"
              onClick={onSave}
              disabled={loading || saveDisabled}
              loadingPosition="start"
              startIcon={saveIcon === undefined ? <Save /> : saveIcon}
              fullWidth={isMobile}
              color={saveColor}
            >
              {saveText ?? t('Save')}
            </LoadingButton>
          ))}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      onClose={(_, reason) => {
        if (
          loading &&
          (reason === 'backdropClick' || reason === 'escapeKeyDown')
        ) {
          return;
        }
        onClose();
      }}
      sx={[
        {
          '& .MuiDialog-paper': {
            backgroundColor: customPalette.superLightBlue,
            display: 'flex',
            flexDirection: 'column',
            ...(dialogProps?.fullScreen === true
              ? {}
              : {
                  maxHeight: '90vh',
                  '@supports (max-height: 90dvh)': {
                    maxHeight: '90dvh',
                  },
                }),
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...dialogProps}
      slots={{
        transition: SlideUpTransition,
        ...(dialogProps?.slots ?? {}),
      }}
    >
      <DialogStickyHeader
        title={title}
        onClose={onClose}
        loading={loading}
        sx={{ flexShrink: 0 }}
      />
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {children}
      </Box>
      <Box sx={{ flexShrink: 0 }}>{footerActions ?? defaultFooter}</Box>
    </Dialog>
  );
};
